'use client';

import {
  ArrowDown,
  ArrowElbowDownLeft,
  ArrowUp,
  MagnifyingGlass,
} from '@phosphor-icons/react/ssr';
import { defaultFilter } from 'cmdk';
import {
  type ComponentProps,
  type ElementType,
  type KeyboardEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';

import { tv } from '../../lib/tv';
import { cn } from '../../lib/utils';
import {
  Command,
  CommandGroup,
  CommandItem as CmdkItem,
  CommandList,
} from '../ui/command';
import { Dialog, DialogContent, DialogTitle, DialogTrigger } from '../ui/dialog';
import { useCmdkActiveDescendant } from './cmdk-active-descendant';
import { Divider } from './divider';
import { Kbd, type KbdProps } from './kbd';
import { menuStyles } from './menu-styles';

/** A shortcut hint on a Command Item, e.g. `{ modifier: 'command', key: 'G' }` = ⌘G. */
export type CommandShortcut = { key: string; modifier?: KbdProps['modifier'] };

/** One command: an action, a reel, a person or a page. */
export type CommandPaletteItem = {
  /** Unique id; what `onSelect` and the recent list keep. */
  value: string;
  /** Figma "Label". Names the option and is searched. */
  label: string;
  /** Figma "Description text" (Description=true): a second line, also searched. */
  description?: string;
  /** Figma "Icon": the 16px glyph in the 28px tile. */
  icon: ElementType<{ className?: string }>;
  /** Figma "Shortcut": a Kbd hint (exposed as `aria-keyshortcuts`; the app binds the key). */
  shortcut?: CommandShortcut;
  /** More words that match the query (synonyms, an email). */
  keywords?: string[];
  disabled?: boolean;
  /** Run the command. The palette closes afterwards. */
  onSelect?: () => void;
};

/** A titled group (Figma Menu Header), e.g. Actions · Reels · People · Go to. */
export type CommandPaletteGroup = { heading: string; items: CommandPaletteItem[] };

/**
 * Figma Command Item (99:43): 44 tall (52 with a description), 8 · 12
 * padding, gap 12, radius/md; Active = bg/muted. A 28px icon tile (bg/subtle
 * → bg/surface when Active, 1px border/subtle inside, radius/sm) with a 16px
 * icon/secondary glyph; Label Body/Medium Strong text/primary; Description
 * Caption text/tertiary; a Small Combo Kbd shortcut; the Small ↵ Kbd hint
 * only on the Active row.
 */
export const commandItemStyles = tv({
  slots: {
    root: [
      'group/command h-auto gap-3 rounded-md px-3 py-2',
      'data-[disabled=true]:opacity-100',
    ],
    tile: [
      'bg-bg-subtle flex size-7 shrink-0 items-center justify-center rounded-sm border border-border-subtle',
      'group-data-[selected=true]/command:bg-bg-surface',
    ],
    icon: 'text-icon-secondary size-4',
    text: 'flex min-w-0 flex-1 flex-col',
    label:
      'text-body-md-strong text-text-primary truncate group-data-[disabled=true]/command:text-text-disabled',
    // text/tertiary Caption on the Active row's bg/muted is 4.49:1 - one step darker there (WCAG AA).
    description:
      'text-caption text-text-tertiary truncate group-data-[selected=true]/command:text-text-secondary',
    shortcut: 'flex shrink-0',
    enter: 'hidden shrink-0 group-data-[selected=true]/command:flex',
  },
});

const KEY_NAMES: Record<string, string> = {
  command: 'Meta',
  cmd: 'Meta',
  ctrl: 'Control',
  control: 'Control',
  shift: 'Shift',
  alt: 'Alt',
  option: 'Alt',
};

/** `{ modifier: 'command', key: 'G' }` → "Meta+G" (the `aria-keyshortcuts` syntax). */
export function toKeyShortcut({ key, modifier }: CommandShortcut) {
  const mod = modifier ? (KEY_NAMES[modifier.toLowerCase()] ?? modifier) : undefined;
  return mod ? `${mod}+${key}` : key;
}

export type CommandItemProps = Omit<
  ComponentProps<typeof CmdkItem>,
  'children' | 'value' | 'onSelect'
> & {
  value: string;
  label: string;
  description?: string;
  icon: ElementType<{ className?: string }>;
  shortcut?: CommandShortcut;
  onSelect?: (value: string) => void;
};

/**
 * A row in the Command Palette (Figma: Components ↳ Command Palette ↳
 * Command Item). A cmdk option: it must render inside a `Command` › list.
 * Named by its label, described by its description; the shortcut and ↵
 * hints are hidden from assistive tech (the shortcut is `aria-keyshortcuts`).
 */
export function CommandItem({
  label,
  description,
  icon: Icon,
  shortcut,
  className,
  ...props
}: CommandItemProps) {
  const styles = commandItemStyles();
  const id = useId();
  return (
    <CmdkItem
      data-slot="command-palette-item"
      aria-labelledby={`${id}-label`}
      aria-describedby={description ? `${id}-description` : undefined}
      aria-keyshortcuts={shortcut ? toKeyShortcut(shortcut) : undefined}
      className={styles.root({ className })}
      {...props}
    >
      <span aria-hidden data-slot="command-palette-item-tile" className={styles.tile()}>
        <Icon className={styles.icon()} />
      </span>
      <span className={styles.text()}>
        <span
          id={`${id}-label`}
          data-slot="command-palette-item-label"
          className={styles.label()}
        >
          {label}
        </span>
        {description && (
          <span
            id={`${id}-description`}
            data-slot="command-palette-item-description"
            className={styles.description()}
          >
            {description}
          </span>
        )}
      </span>
      {shortcut && (
        <span
          aria-hidden
          data-slot="command-palette-item-shortcut"
          className={styles.shortcut()}
        >
          <Kbd modifier={shortcut.modifier}>{shortcut.key}</Kbd>
        </span>
      )}
      <span aria-hidden data-slot="command-palette-item-enter" className={styles.enter()}>
        <Kbd icon={ArrowElbowDownLeft}>Enter</Kbd>
      </span>
    </CmdkItem>
  );
}

/**
 * Figma Command Palette (99:229): 640 wide, bg/surface, 1px border/subtle
 * (inside), radius/xl, Shadow/Overlay, clipped. Search 56 (20 left, 16
 * right, gap 12: 20px icon/secondary glyph, Body/Large query with a
 * border/focus caret, Esc Kbd) · Subtle Divider · Results (8px padding;
 * Menu Headers 28 with 12 left) · Footer 41 (bg/subtle, 1px border/subtle
 * top, 10 · 16, gap 16: ↑↓ Navigate, ↵ Open, Esc Close - Kbd + Caption
 * text/tertiary, gap 4). Empty: 40px above and below, gap 6: a 24px
 * icon/tertiary glyph, Body/Medium Strong title, Body/Small text/secondary hint.
 */
export const commandPaletteStyles = tv({
  slots: {
    root: [
      'bg-bg-surface text-text-primary flex w-160 max-w-[calc(100vw-32px)] flex-col overflow-hidden outline-none',
      'rounded-xl border border-border-subtle shadow-overlay',
    ],
    search: 'flex h-14 shrink-0 items-center gap-3 pr-4 pl-5',
    searchIcon: 'text-icon-secondary size-5 shrink-0',
    input: [
      'text-body-lg text-text-primary min-w-0 flex-1 bg-transparent outline-none',
      'caret-border-focus placeholder:text-text-tertiary',
      '[&::-webkit-search-cancel-button]:appearance-none',
    ],
    divider: 'shrink-0',
    results: 'min-h-0 flex-1 overflow-y-auto p-2',
    header: 'pr-2 pl-3',
    empty: 'flex flex-col items-center gap-1.5 py-10 text-center',
    emptyIcon: 'text-icon-tertiary size-6',
    emptyTitle: 'text-body-md-strong text-text-primary',
    emptyHint: 'text-body-sm text-text-secondary',
    footer:
      'bg-bg-subtle flex shrink-0 items-center gap-4 border-t border-border-subtle px-4 py-2.5',
    hint: 'flex items-center gap-1',
    hintText: 'text-caption text-text-tertiary',
  },
});

/** Palette 96px from the top, centred; fade + scale 0.98 → 1, 150ms. */
const paletteMotion = [
  'fixed inset-x-0 top-24 z-50 mx-auto max-h-[calc(100dvh-112px)]',
  'ease-out data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-98 data-[state=open]:duration-150',
  'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-98 data-[state=closed]:duration-150',
].join(' ');

/**
 * The example's backdrop: bg/inverse (Ink) at 32% layer opacity - lighter
 * than the Modal's bg/overlay (48%). The fade-in ends at this opacity.
 */
const paletteBackdrop = 'bg-bg-inverse opacity-32';

/** Default match: cmdk's fuzzy score over the label, description and keywords. */
export function matchCommand(item: CommandPaletteItem, query: string) {
  return (
    defaultFilter(item.label, query.trim(), [
      ...(item.keywords ?? []),
      ...(item.description ? [item.description] : []),
    ]) > 0
  );
}

const RECENT = 'recent:';

type BodyProps = {
  groups: CommandPaletteGroup[];
  /** Values of recently run commands, newest first (shown for the empty query). */
  recent?: string[];
  recentHeading?: string;
  defaultQuery?: string;
  filter?: (item: CommandPaletteItem, query: string) => boolean;
  onSelect?: (item: CommandPaletteItem) => void;
  placeholder?: string;
  inputLabel?: string;
  emptyTitle?: (query: string) => ReactNode;
  emptyHint?: ReactNode;
};

function CommandPaletteBody({
  groups,
  recent = [],
  recentHeading = 'Recent',
  defaultQuery = '',
  filter = matchCommand,
  onSelect,
  placeholder = 'Search or run a command…',
  inputLabel = 'Search commands',
  emptyTitle = (q) => `No results for “${q}”`,
  emptyHint = 'Try a reel name, a teammate, or an action like “upload”.',
}: BodyProps) {
  const styles = commandPaletteStyles();
  const [query, setQuery] = useState(defaultQuery);
  const q = query.trim();

  const byValue = new Map(groups.flatMap((g) => g.items.map((i) => [i.value, i])));
  // Recent commands (empty query only), then the groups in order, filtered.
  const visible = (text: string) => {
    const t = text.trim();
    const recentItems = t
      ? []
      : recent.flatMap((v) => {
          const item = byValue.get(v);
          return item ? [{ ...item, value: RECENT + v }] : [];
        });
    return [
      ...(recentItems.length ? [{ heading: recentHeading, items: recentItems }] : []),
      ...groups.map((g) => ({
        ...g,
        items: t ? g.items.filter((i) => filter(i, t)) : g.items,
      })),
    ].filter((g) => g.items.length);
  };
  const first = (list: CommandPaletteGroup[]) =>
    list.flatMap((g) => g.items).find((i) => !i.disabled)?.value ?? '';
  const shown = visible(query);

  // First result Active; follows the query.
  const [active, setActive] = useState(() => first(shown));
  const [listId, setListId] = useState<string>();
  const attachList = useCallback((el: HTMLDivElement | null) => {
    if (el) setListId(el.id);
  }, []);
  const ids = useCmdkActiveDescendant(listId, true);
  const empty = shown.length === 0;

  const run = (value: string) => {
    const item = byValue.get(
      value.startsWith(RECENT) ? value.slice(RECENT.length) : value,
    );
    if (item && !item.disabled) onSelect?.(item);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    // Home / End move the caret in the query, not the active option.
    if (e.key === 'Home' || e.key === 'End') e.stopPropagation();
  };

  return (
    <Command
      shouldFilter={false}
      loop
      vimBindings={false}
      value={active}
      onValueChange={setActive}
      label={inputLabel}
      data-slot="command-palette-command"
      className="flex min-h-0 flex-1 flex-col"
    >
      <div data-slot="command-palette-search" className={styles.search()}>
        <MagnifyingGlass aria-hidden className={styles.searchIcon()} />
        <input
          type="text"
          role="combobox"
          aria-expanded
          aria-controls={ids.list}
          aria-activedescendant={ids.active}
          aria-autocomplete="list"
          aria-label={inputLabel}
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
          data-slot="command-palette-input"
          placeholder={placeholder}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setActive(first(visible(e.target.value)));
          }}
          onKeyDown={handleKeyDown}
          className={styles.input()}
        />
        <Kbd aria-hidden data-slot="command-palette-esc">
          Esc
        </Kbd>
      </div>
      <Divider
        tone="subtle"
        data-slot="command-palette-divider"
        className={styles.divider()}
      />
      <div data-slot="command-palette-results" className={styles.results()}>
        <CommandList ref={attachList} label="Results" data-slot="command-palette-list">
          {shown.map((group) => (
            <CommandGroup
              key={group.heading}
              heading={
                <span
                  data-slot="command-palette-header"
                  className={menuStyles().header({ className: styles.header() })}
                >
                  {group.heading}
                </span>
              }
            >
              {group.items.map((item) => (
                <CommandItem
                  key={item.value}
                  value={item.value}
                  label={item.label}
                  description={item.description}
                  icon={item.icon}
                  shortcut={item.shortcut}
                  disabled={item.disabled}
                  onSelect={run}
                />
              ))}
            </CommandGroup>
          ))}
        </CommandList>
        <div role="status" data-slot="command-palette-status">
          {empty && (
            <div data-slot="command-palette-empty" className={styles.empty()}>
              <MagnifyingGlass aria-hidden className={styles.emptyIcon()} />
              <span className={styles.emptyTitle()}>{emptyTitle(q)}</span>
              {emptyHint && <span className={styles.emptyHint()}>{emptyHint}</span>}
            </div>
          )}
        </div>
      </div>
      <div data-slot="command-palette-footer" className={styles.footer()}>
        <span className={styles.hint()}>
          <Kbd icon={ArrowUp}>Up arrow</Kbd>
          <Kbd icon={ArrowDown}>Down arrow</Kbd>
          <span className={styles.hintText()}>Navigate</span>
        </span>
        <span className={styles.hint()}>
          <Kbd icon={ArrowElbowDownLeft}>Enter</Kbd>
          <span className={styles.hintText()}>Open</span>
        </span>
        <span className={styles.hint()}>
          <Kbd>Esc</Kbd>
          <span className={styles.hintText()}>Close</span>
        </span>
      </div>
    </Command>
  );
}

/** ⌘ / Ctrl + `key`, no Alt or Shift. */
const isShortcut = (e: globalThis.KeyboardEvent, key: string) =>
  (e.metaKey || e.ctrlKey) && !e.altKey && !e.shiftKey && e.key.toLowerCase() === key;

export type UseCommandPaletteOptions = {
  /** The key that toggles the palette with ⌘ (or Ctrl). Default "k"; `false` = none. */
  shortcut?: string | false;
  defaultOpen?: boolean;
};

/**
 * Open state for a Command Palette plus its global ⌘K / Ctrl+K toggle.
 * The listener is on `window` in the capture phase and calls
 * `preventDefault`, so it runs before - and wins over - a SearchField's ⌘K
 * (which ignores handled events): with both on a page, ⌘K opens the palette.
 *
 *   const palette = useCommandPalette();
 *   <Button onClick={() => palette.setOpen(true)}>Search</Button>
 *   <CommandPalette open={palette.open} onOpenChange={palette.setOpen} … />
 */
export function useCommandPalette({
  shortcut = 'k',
  defaultOpen = false,
}: UseCommandPaletteOptions = {}) {
  const [open, setOpen] = useState(defaultOpen);
  const toggle = useCallback(() => setOpen((o) => !o), []);
  useEffect(() => {
    if (!shortcut) return;
    const key = shortcut.toLowerCase();
    const onKeyDown = (e: globalThis.KeyboardEvent) => {
      if (e.defaultPrevented || e.isComposing || !isShortcut(e, key)) return;
      e.preventDefault();
      toggle();
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [shortcut, toggle]);
  return { open, setOpen, toggle };
}

export type CommandPaletteProps = Omit<BodyProps, 'recent' | 'defaultQuery'> & {
  /** Open state (controlled - pair with `useCommandPalette`). Uncontrolled, the palette registers ⌘K itself. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  defaultOpen?: boolean;
  /** Uncontrolled only: the ⌘ key that toggles it. Default "k"; `false` = none. */
  shortcut?: string | false;
  /** An optional visible opener (a Button or SearchField-like trigger), rendered `asChild`. */
  trigger?: ReactNode;
  /** Names the dialog. */
  label?: string;
  /** Recently run command values, newest first (controlled). Remembered in memory by default. */
  recent?: string[];
  onRecentChange?: (recent: string[]) => void;
  /** How many recent commands to keep. Default 5. */
  recentLimit?: number;
  className?: string;
};

/**
 * ⌘K anywhere: one box to find reels, people and pages and to run actions
 * (Figma: Components ↳ Command Palette). A Radix Dialog (focus trapped, Esc
 * closes, focus returns to where it was) holding a cmdk list: a real
 * `role="combobox"` input that `aria-controls` the `listbox` and points
 * `aria-activedescendant` at the Active option. Results are grouped under
 * headers in the order given (put Actions first when the query is a verb),
 * fuzzy matched, first result Active; ↑ / ↓ move (wrapping), Enter runs.
 * The empty query shows recent commands first. No match shows a polite
 * status.
 */
export function CommandPalette({
  open: openProp,
  onOpenChange,
  defaultOpen,
  shortcut = 'k',
  trigger,
  label = 'Command palette',
  recent: recentProp,
  onRecentChange,
  recentLimit = 5,
  onSelect,
  className,
  ...body
}: CommandPaletteProps) {
  const controlled = openProp !== undefined;
  const own = useCommandPalette({ shortcut: controlled ? false : shortcut, defaultOpen });
  const open = controlled ? openProp : own.open;
  const setOpen = (next: boolean) => {
    if (!controlled) own.setOpen(next);
    onOpenChange?.(next);
  };
  const [recentState, setRecentState] = useState<string[]>([]);
  const recent = recentProp ?? recentState;
  // Opened by ⌘K there is no trigger for Radix to refocus: return focus to
  // whatever had it when the palette opened.
  const returnTo = useRef<HTMLElement | null>(null);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}
      <DialogContent
        aria-describedby={undefined}
        overlayClassName={paletteBackdrop}
        data-slot="command-palette"
        onOpenAutoFocus={() => {
          returnTo.current = document.activeElement as HTMLElement | null;
        }}
        onCloseAutoFocus={(event) => {
          const el = returnTo.current;
          if (el?.isConnected && el !== document.body) {
            event.preventDefault();
            el.focus();
          }
        }}
        className={commandPaletteStyles().root({
          className: cn(paletteMotion, className),
        })}
      >
        <DialogTitle className="sr-only">{label}</DialogTitle>
        <CommandPaletteBody
          {...body}
          recent={recent}
          onSelect={(item) => {
            const next = [item.value, ...recent.filter((v) => v !== item.value)].slice(
              0,
              recentLimit,
            );
            setRecentState(next);
            onRecentChange?.(next);
            setOpen(false);
            item.onSelect?.();
            onSelect?.(item);
          }}
        />
      </DialogContent>
    </Dialog>
  );
}

export type CommandPalettePanelProps = BodyProps & { className?: string };

/**
 * The palette drawn in place (docs, the Figma frame): the same surface and
 * list, without the dialog, backdrop or focus trap. Still interactive.
 */
export function CommandPalettePanel({ className, ...body }: CommandPalettePanelProps) {
  return (
    <div
      data-slot="command-palette"
      className={commandPaletteStyles().root({ className })}
    >
      <CommandPaletteBody {...body} />
    </div>
  );
}
